import FormSelect from 'components/Select/FormSelect';
import Text from 'components/Text/Text';
import { ITotalPrice, IItemForGetShortInfoWithStatus } from 'types';
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useArchivedMultiSelect } from 'hooks/useArchivedMultiSelect';
import { Checkbox } from 'antd';
import SearchField from 'components/SearchField/SearchField';
import style from './MultipleSelect.module.scss';

interface IMultipleSelect {
	setQuantityRegulationRestaurants: (value: string[]) => void;
	quantityRegulationRestaurants: string[];
	totalPrices: ITotalPrice[];
}

const MultipleSelect: React.FC<IMultipleSelect> = ({
	setQuantityRegulationRestaurants,
	quantityRegulationRestaurants,
	totalPrices,
}) => {
	const [searchString, setSearchString] = useState<string>('');

	const items = useMemo<IItemForGetShortInfoWithStatus[]>(
		() =>
			totalPrices.map((el) => ({
				id: el.restaurantId,
				name: el.restaurantName,
				status: el.restaurantStatus,
			})),
		[totalPrices],
	);

	const { showArchived, toggleShowArchived, visibleItems, options } = useArchivedMultiSelect({
		items,
		entityType: 'restaurants',
		selectedIds: quantityRegulationRestaurants,
	});

	const filteredData = useMemo(() => {
		return searchString
			? options.filter((el) => el.name.toLowerCase().includes(searchString.toLowerCase()))
			: options;
	}, [searchString, options]);

	// ----- where roles are used, the selection of all is prohibited ----- //
	const handleSelectAllData = useCallback(() => {
		if (visibleItems.length && visibleItems.length === quantityRegulationRestaurants.length) {
			setQuantityRegulationRestaurants([]);
		} else {
			setQuantityRegulationRestaurants(visibleItems.map((el) => el.id));
		}
	}, [visibleItems, quantityRegulationRestaurants, setQuantityRegulationRestaurants]);
	// ----- where roles are used, the selection of all is prohibited ----- //

	useEffect(() => {
		setSearchString('');
	}, [totalPrices]);

	const renderSelectMenu = useCallback(
		(menu: React.ReactElement) => {
			return (
				<>
					<div className={style.selectMenuHeader}>
						<Checkbox
							className={style.checkBox}
							checked={
								!!(visibleItems.length && visibleItems.length === quantityRegulationRestaurants.length)
							}
							disabled={!visibleItems.length}
							onChange={handleSelectAllData}>
							<Text
								translationNameSpace="form"
								translationKey="selectAllRestaurantsLabel"
								asFragment={true}
							/>
						</Checkbox>
						<Checkbox className={style.checkBox} checked={showArchived} onChange={toggleShowArchived}>
							<Text translationNameSpace="modals" translationKey="showArchived" asFragment={true} />
						</Checkbox>
						<SearchField
							onChangeSelectSearchString={setSearchString}
							searchString={searchString}
							className={style.searchField}
						/>
					</div>
					{menu}
				</>
			);
		},
		[
			visibleItems,
			quantityRegulationRestaurants,
			handleSelectAllData,
			searchString,
			setSearchString,
			showArchived,
			toggleShowArchived,
		],
	);

	return (
		<FormSelect
			name="quantityRegulationRestaurants"
			label="restaurantsLabel"
			options={filteredData}
			onChange={setQuantityRegulationRestaurants}
			disabled={!totalPrices.length}
			dropdownRender={renderSelectMenu}
			mode="multiple"
			showSearch={false}
			counterValue={quantityRegulationRestaurants.length}
		/>
	);
};

export default React.memo(MultipleSelect);
